import React from 'react';

import { ReactComponent as ArrowIcon } from 'assets/pics/arrowDownIcon.svg';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { FormSection } from 'components/FormHelper/FormSection';
import { InputField } from 'components/FormHelper/InputField';
import { UploadField } from 'components/FormHelper/UploadField/UploadField';
import { HintText } from 'components/HintText/HintText';
import { HINTTEXT_TYPE } from 'components/HintText/HintTextType';
import { MessageFields } from 'components/MyTaskArea/TaskDetailPage/MessageFields';
import { MessageHeader } from 'components/MyTaskArea/TaskDetailPage/MessageHeader';
import { MessageType } from 'components/MyTaskArea/types';
import { SubTitle } from 'components/SubTitle/SubTitle';

import { FILETYPE_DOCUMENTS } from 'global/SharedDocumentFields';

import './LastMessage.sass';

export const LastMessage = ({
  lastMessage,
  formik,
  isSubmitting,
  submitError,
  submitSuccess,
  entityNameMap,
  comboBoxOptions,
}) => {
  const { message, senderLabel, timestamp, fields, files } = lastMessage;
  const { values } = formik;

  const scrollToComments = () => {
    window.scrollTo({
      top: document.getElementById('task-comments').offsetTop - 100,
      behavior: 'smooth',
    });
  };

  return (
    <div className="last-message">
      <MessageHeader senderLabel={senderLabel} timestamp={timestamp} />
      {message && <p>{message}</p>}
      <div className="task-details__jump-to-comments">
        <button
          type="button"
          className="task-details__jump-to-comments__trigger"
          onClick={scrollToComments}>
          Zum Nachrichtenverlauf springen
        </button>{' '}
        <ArrowIcon className="arrow-icon" onClick={scrollToComments} />
      </div>
      {fields?.length > 0 && (
        <div>
          <SubTitle text="Formularfeld Angaben" notFramed />
          <p>Bitte füllen Sie die angeforderten Felder aus.</p>
          <MessageFields
            fields={fields}
            formik={formik}
            entityNameMap={entityNameMap}
            comboBoxOptions={comboBoxOptions}
          />
        </div>
      )}
      {files?.length > 0 && (
        <div>
          <SubTitle text="Dokument(e) hochladen" notFramed />
          <p>Bitte laden Sie die hier aufgelisteten Dokumente erneut hoch.</p>
          <div className="document-list">
            {files.map((fileEntry) => {
              const fileObject = FILETYPE_DOCUMENTS[fileEntry.documentType];
              if (!fileObject) {
                return null;
              }
              return (
                <FormSection key={fileEntry.id} subtitle={`· ${fileObject.title} *`}>
                  <UploadField
                    infoText={fileObject.infoText}
                    name={fileObject.name}
                    optional={false}
                    files={values[fileObject.name] || []}
                    formik={formik}
                  />
                </FormSection>
              );
            })}
          </div>
        </div>
      )}
      <FormSection key="changes">
        <InputField
          name="changes"
          fullWidth
          noMaxWidth
          label="Weitere Anmerkungen"
          oldValue=""
          variant="outlined"
          value={values.changes}
          onChange={formik.handleChange}
          error={Boolean(formik.errors.changes)}
          helperText={formik.errors.changes || ''}
        />
      </FormSection>
      <div className="document-list__send">
        {submitError && <HintText type={HINTTEXT_TYPE.ERROR}>{submitError}</HintText>}
        {submitSuccess && <HintText>Ihre Daten wurden erfolgreich übermittelt.</HintText>}
        <ActionButton
          title={isSubmitting ? 'Wird gesendet…' : 'Antwort senden'}
          onClick={formik.submitForm}
          inverted
          disabled={!formik.isValid || isSubmitting || submitSuccess}
        />
      </div>
    </div>
  );
};

LastMessage.propTypes = {
  lastMessage: MessageType.isRequired,
  formik: PropTypes.object.isRequired,
  entityNameMap: PropTypes.object,
  isSubmitting: PropTypes.bool,
  submitError: PropTypes.string,
  submitSuccess: PropTypes.bool,
  comboBoxOptions: PropTypes.object,
};

LastMessage.defaultProps = {
  isSubmitting: false,
  submitError: null,
  submitSuccess: false,
  entityNameMap: {},
  comboBoxOptions: {},
};
